
import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import ProductCard from "@/components/ProductCard";
import ProductsHeader from "@/components/products/ProductsHeader";
import CategoryFilter from "@/components/products/CategoryFilter";
import { useProducts } from "@/hooks/useProducts";
import { useCategories } from "@/hooks/useCategories";
import { useToast } from "@/components/ui/use-toast";

const Index = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const { data: products, isLoading, error, refetch } = useProducts();
  const { data: categories = [], isLoading: categoriesLoading } = useCategories();
  const { toast } = useToast();

  // Effet pour actualiser les données au premier chargement
  useEffect(() => {
    console.log("🔄 Index: Rafraîchissement des données au montage");
    refetch();
  }, [refetch]);

  console.log("🏠 PAGE INDEX RENDUE");
  console.log("📦 PRODUITS REÇUS:", products?.length);
  console.log("📑 CATÉGORIES REÇUES:", categories);
  console.log("⏳ CHARGEMENT:", isLoading);
  
  if (error) {
    console.error("❌ ERREUR:", error);
    toast({
      title: "Erreur de chargement",
      description: "Impossible de charger les produits. Veuillez réessayer.",
      variant: "destructive"
    });
  }

  // Filtrer les produits par catégorie sélectionnée
  const filteredProducts = selectedCategory
    ? products?.filter((product) => {
        return product.categories.includes(selectedCategory);
      })
    : products;

  // Meta description based on available products
  const metaDescription = "Découvrez notre sélection de vêtements pratiques et stylés, disponibles en différentes tailles et conçus pour répondre à vos besoins quotidiens. Que vous cherchiez des hoodies, des vêtements de travail ou des accessoires, nous avons ce qu'il vous faut.";
  
  // Get current URL for meta tags
  const currentUrl = window.location.href;

  return (
    <>
      <Helmet>
        <title>Agroref | Collection de Vêtements et Accessoires</title>
        <meta name="description" content={metaDescription} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content="Agroref | Collection de Vêtements et Accessoires" />
        <meta property="og:description" content={metaDescription} />
        {products?.[0]?.images[0] && (
          <meta property="og:image" content={products[0].images[0]} />
        )}
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={currentUrl} />
        <meta property="twitter:title" content="Agroref | Collection de Vêtements et Accessoires" />
        <meta property="twitter:description" content={metaDescription} />
        {products?.[0]?.images[0] && (
          <meta property="twitter:image" content={products[0].images[0]} />
        )}
        
        {/* Structured data for e-commerce site */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Agroref",
            "url": currentUrl,
            "potentialAction": {
              "@type": "SearchAction",
              "target": `${window.location.origin}/search?q={search_term_string}`,
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
      </Helmet>
      
      <div className="container mx-auto px-4 py-6 sm:py-8">
        <ProductsHeader
          title="Agroref"
          subtitle="Collection de Vêtements et Accessoires"
          description="Découvrez notre sélection de vêtements pratiques et stylés, disponibles en différentes tailles
            et conçus pour répondre à vos besoins quotidiens. Que vous cherchiez des
            hoodies, des vêtements de travail ou des accessoires comme nos tasses et casquettes, nous avons ce qu'il vous faut."
        />

        {categories && categories.length > 0 && (
          <CategoryFilter
            categories={categories}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
          />
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 mt-6 sm:mt-8">
          {isLoading || categoriesLoading ? (
            // Skeleton loading state
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-muted aspect-square rounded-lg"></div>
                <div className="mt-4 h-4 bg-muted rounded w-3/4"></div>
                <div className="mt-2 h-4 bg-muted rounded w-1/4"></div>
              </div>
            ))
          ) : (
            filteredProducts?.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default Index;
